import { useEffect, useState, type CSSProperties } from 'react';

/**
 * Short celebration bursts (confetti / stars) for milestones — mastery, level-ups,
 * achievements and answer streaks. Pure CSS particles, removed after ~1.3 s;
 * nothing is shown when motion is off or reduced.
 */
interface Bit { id: number; x: number; y: number; dx: number; dy: number; rot: number; c: string; shape: string; dur: number; delay: number }
type Listener = (bits: Bit[]) => void;

const COLORS = ['#46e6ff', '#a283ff', '#ff4fd8', '#ffb84d', '#48f5b0', '#ffe38a'];
const listeners = new Set<Listener>();
let seq = 0;

export function burst(o: { x?: number; y?: number; count?: number; kind?: 'confetti' | 'stars' } = {}) {
  const motion = typeof document !== 'undefined' ? document.documentElement.dataset.motion : 'full';
  if (motion !== 'full') return;
  const x = o.x ?? window.innerWidth / 2;
  const y = o.y ?? window.innerHeight * 0.42;
  const n = Math.min(120, o.count ?? 40);
  const bits: Bit[] = Array.from({ length: n }, () => {
    const a = Math.random() * Math.PI * 2;
    const r = 140 + Math.random() * 320;
    return {
      id: ++seq, x, y,
      dx: Math.cos(a) * r, dy: Math.sin(a) * r * 0.75 + 160 + Math.random() * 120,
      rot: (Math.random() - 0.5) * 900,
      c: COLORS[Math.floor(Math.random() * COLORS.length)],
      shape: o.kind === 'stars' ? (Math.random() < 0.7 ? 'star' : 'round') : Math.random() < 0.35 ? 'round' : '',
      dur: 900 + Math.random() * 600, delay: Math.random() * 120,
    };
  });
  listeners.forEach((fn) => fn(bits));
}

export function BurstLayer() {
  const [bits, setBits] = useState<Bit[]>([]);
  useEffect(() => {
    const fn: Listener = (add) => {
      setBits((b) => [...b.slice(-200), ...add]);
      const ids = new Set(add.map((x) => x.id));
      setTimeout(() => setBits((b) => b.filter((x) => !ids.has(x.id))), 1800);
    };
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  if (!bits.length) return null;
  return (
    <div className="burst-layer" aria-hidden="true">
      {bits.map((b) => (
        <span
          key={b.id}
          className={`burst-bit ${b.shape}`}
          style={{
            left: b.x, top: b.y, background: b.c, ['--c' as string]: b.c,
            ['--dx' as string]: `${b.dx}px`, ['--dy' as string]: `${b.dy}px`, ['--rot' as string]: `${b.rot}deg`,
            ['--dur' as string]: `${b.dur}ms`, animationDelay: `${b.delay}ms`,
          } as CSSProperties}
        />
      ))}
    </div>
  );
}
